import { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Save, Power, PowerOff, History, Clock, RotateCcw, ListChecks, Sparkles } from 'lucide-react';
import useAutomationsStore from '../../../../../store/useAutomationsStore';

export default function EditorTopBar({ workflow, onBack, onSave, onToggleStatus, onLoadVersion, onRestoreLatest, showExecutions, onToggleExecutions, showAIChat, onToggleAIChat }) {
  const [showVersions, setShowVersions] = useState(false);
  const versionsRef = useRef(null);
  const { currentVersionId } = useAutomationsStore();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (versionsRef.current && !versionsRef.current.contains(e.target)) {
        setShowVersions(false);
      }
    };
    if (showVersions) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showVersions]);

  if (!workflow) return null;

  const isActive = workflow.active;
  const versions = workflow.versions || [];
  const isViewingOldVersion = !!currentVersionId;

  return (
    <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-gray-200 shrink-0 z-10">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 transition">
          <ArrowLeft className="w-4 h-4 text-gray-600" />
        </button>
        <div>
          <h2 className="text-sm font-semibold text-gray-800">{workflow.name}</h2>
          {workflow.description && (
            <p className="text-[11px] text-gray-400">{workflow.description}</p>
          )}
        </div>
        <span
          className={[
            'text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide',
            isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500',
          ].join(' ')}
        >
          {isActive ? 'Active' : 'Draft'}
        </span>
        {isViewingOldVersion && (
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 uppercase tracking-wide">
            Viewing old version
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {/* Restore to latest when viewing old version */}
        {isViewingOldVersion && (
          <button
            onClick={onRestoreLatest}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Back to latest
          </button>
        )}

        {/* Version History */}
        {versions.length > 0 && (
          <div className="relative" ref={versionsRef}>
            <button
              onClick={() => setShowVersions(!showVersions)}
              className={[
                'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition',
                showVersions
                  ? 'text-blue-700 bg-blue-50'
                  : 'text-gray-600 bg-gray-50 hover:bg-gray-100',
              ].join(' ')}
            >
              <History className="w-3.5 h-3.5" />
              Versions
              <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full ml-0.5">
                {versions.length}
              </span>
            </button>

            {showVersions && (
              <div className="absolute right-0 top-10 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50 max-h-80 overflow-y-auto">
                <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                  <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Version History</p>
                  <p className="text-[10px] text-gray-400">{versions.length} versions</p>
                </div>
                <div className="p-1">
                  {versions.map((version, index) => {
                    const isLatest = index === 0;
                    const isCurrent = isLatest
                      ? !currentVersionId
                      : currentVersionId === version.versionId;
                    const date = new Date(version.lastModified || version.date);
                    const dateStr = date.toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                    });
                    const timeStr = date.toLocaleTimeString('en-US', {
                      hour: '2-digit', minute: '2-digit',
                    });

                    return (
                      <button
                        key={version.versionId}
                        onClick={() => {
                          onLoadVersion(isLatest ? null : version.versionId);
                          setShowVersions(false);
                        }}
                        className={[
                          'flex items-center gap-3 w-full px-3 py-2.5 text-left rounded-lg transition',
                          isCurrent ? 'bg-blue-50' : 'hover:bg-gray-50',
                        ].join(' ')}
                      >
                        <div className={[
                          'w-2 h-2 rounded-full shrink-0',
                          isCurrent ? 'bg-blue-500' : 'bg-gray-300',
                        ].join(' ')} />
                        <Clock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-xs font-medium text-gray-700">
                              {isLatest ? 'Latest' : `Version ${versions.length - index}`}
                            </p>
                            {isLatest && (
                              <span className="text-[9px] font-medium text-green-600 bg-green-100 px-1.5 py-0.5 rounded-full">
                                Latest
                              </span>
                            )}
                          </div>
                          <p className="text-[10px] text-gray-400">{dateStr} at {timeStr}</p>
                        </div>
                        {isCurrent && (
                          <span className="text-[9px] font-semibold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full shrink-0">
                            Current
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <button
          onClick={onToggleAIChat}
          className={[
            'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition',
            showAIChat
              ? 'text-violet-700 bg-violet-50'
              : 'text-gray-600 bg-gray-50 hover:bg-gray-100',
          ].join(' ')}
        >
          <Sparkles className="w-3.5 h-3.5" />
          AI
        </button>

        <button
          onClick={onToggleExecutions}
          className={[
            'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition',
            showExecutions
              ? 'text-blue-700 bg-blue-50'
              : 'text-gray-600 bg-gray-50 hover:bg-gray-100',
          ].join(' ')}
        >
          <ListChecks className="w-3.5 h-3.5" />
          Executions
        </button>

        <button
          onClick={onToggleStatus}
          className={[
            'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition',
            isActive
              ? 'text-red-600 bg-red-50 hover:bg-red-100'
              : 'text-green-600 bg-green-50 hover:bg-green-100',
          ].join(' ')}
        >
          {isActive ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
          {isActive ? 'Deactivate' : 'Activate'}
        </button>
        <button
          onClick={onSave}
          className="flex items-center gap-1.5 px-4 py-1.5 text-sm text-white bg-[#2461DD] hover:bg-[#1d4fba] rounded-lg transition"
        >
          <Save className="w-3.5 h-3.5" />
          {isViewingOldVersion ? 'Save as Latest' : 'Save'}
        </button>
      </div>
    </div>
  );
}
